import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  CalendarIcon, 
  ClockIcon, 
  MapPinIcon, 
  CurrencyDollarIcon,
  UserIcon,
  UserGroupIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import { API_URL } from '../../config/api';

const BookingView = () => {
  const { viewToken } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (viewToken) {
      fetchBookingDetails();
    }
  }, [viewToken]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Fetching booking details for token: ${viewToken}`);
      
      const response = await fetch(`${API_URL}/public/booking/view/${viewToken}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch booking details');
      }

      console.log('Booking details received:', data);
      setBooking(data.data);
      
    } catch (error) {
      console.error('Error fetching booking details:', error);
      setError(error.message || 'Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  // Status styling
  const getStatusStyle = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'text-green-600 bg-green-100';
      case 'PENDING_PAYMENT':
        return 'text-orange-600 bg-orange-100';
      case 'COMPLETED':
        return 'text-blue-600 bg-blue-100';
      case 'CANCELLED':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'Confirmed';
      case 'PENDING_PAYMENT':
        return 'Pending Payment';
      case 'COMPLETED':
        return 'Completed';
      case 'CANCELLED':
        return 'Cancelled';
      default:
        return status || 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto" />
            <h1 className="mt-4 text-xl font-bold text-gray-900">Booking Not Found</h1>
            <p className="mt-2 text-gray-600">
              {error === 'Booking not found or link expired' 
                ? 'This booking link has expired or is no longer valid.' 
                : error}
            </p>
            <div className="mt-6">
              <p className="text-sm text-gray-500">
                Please check your email for the correct link or contact us for assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No booking data available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="text-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto" />
            <h1 className="mt-4 text-3xl font-bold text-gray-900">Booking Details</h1>
            <p className="mt-2 text-gray-600">
              Reference Number: <span className="font-semibold text-purple-600">{booking.reservationNumber}</span>
            </p>
          </div>
          
          {/* Status Badge */}
          <div className="mt-4 flex justify-center">
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusStyle(booking.status)}`}>
              {getStatusText(booking.status)}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Booking Information */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <InformationCircleIcon className="h-5 w-5 mr-2" />
              Booking Information
            </h2>
            
            <div className="space-y-4">
              {booking.service && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Service</p>
                  <p className="text-lg font-semibold text-gray-900">{booking.service.name}</p>
                  {booking.service.description && (
                    <p className="text-sm text-gray-600 mt-1">{booking.service.description}</p>
                  )}
                </div>
              )}

              {booking.item && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Item</p>
                  <p className="text-lg font-semibold text-gray-900">{booking.item.name}</p>
                  {booking.item.description && (
                    <p className="text-sm text-gray-600 mt-1">{booking.item.description}</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    Start Date & Time
                  </p>
                  <p className="font-semibold text-gray-900">{booking.startDateTime}</p>
                </div>

                {booking.endDateTime && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      End Date & Time
                    </p>
                    <p className="font-semibold text-gray-900">{booking.endDateTime}</p>
                  </div>
                )}
              </div>

              {booking.participantCount && booking.participantCount > 1 && (
                <div>
                  <p className="text-sm font-medium text-gray-500 flex items-center">
                    <UserGroupIcon className="h-4 w-4 mr-1" />
                    Participants
                  </p>
                  <p className="font-semibold text-gray-900">{booking.participantCount}</p>
                </div>
              )}

              {booking.service?.location && (
                <div>
                  <p className="text-sm font-medium text-gray-500 flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    Location
                  </p>
                  <p className="font-semibold text-gray-900">{booking.service.location}</p>
                </div>
              )}

              {booking.bookedSlots && booking.bookedSlots.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Time Slots</p>
                  <div className="mt-1 space-y-1">
                    {booking.bookedSlots.map((slot, index) => (
                      <p key={index} className="text-sm font-medium text-gray-700 bg-gray-50 px-2 py-1 rounded">
                        {slot}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-500 flex items-center">
                  <UserIcon className="h-4 w-4 mr-1" />
                  Customer
                </p>
                <p className="font-semibold text-gray-900">{booking.customerName}</p>
              </div>

              {booking.createdAt && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Booked On</p>
                  <p className="font-semibold text-gray-900">{booking.createdAt}</p>
                </div>
              )}
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 mr-2" />
              Payment Summary
            </h2>
            
            <div className="space-y-3">
              {booking.subtotal && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-semibold">${booking.subtotal.toFixed(2)}</span>
                </div>
              )}

              {booking.taxRate && booking.taxAmount && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax ({booking.taxRate}%):</span>
                  <span className="font-semibold">${booking.taxAmount.toFixed(2)}</span>
                </div>
              )}

              {booking.totalAmount && (
                <div className="flex justify-between text-lg font-bold border-t pt-3">
                  <span>Total Amount:</span>
                  <span className="text-green-600">${booking.totalAmount.toFixed(2)}</span>
                </div>
              )}

              {booking.depositAmount && booking.depositAmount > 0 && (
                <div className="flex justify-between text-sm bg-blue-50 p-2 rounded">
                  <span className="text-blue-700">Deposit Paid:</span>
                  <span className="font-semibold text-blue-700">${booking.depositAmount.toFixed(2)}</span>
                </div>
              )}

              {booking.refundAmount && booking.refundAmount > 0 && (
                <div className="flex justify-between text-sm bg-green-50 p-2 rounded">
                  <span className="text-green-700">Refund Amount:</span>
                  <span className="font-semibold text-green-700">${booking.refundAmount.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Additional Information */}
        {(booking.notes || booking.createdBy) && (
          <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              Additional Information
            </h2>
            
            {booking.notes && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-500 mb-2">Notes</p>
                <p className="text-gray-700 whitespace-pre-wrap">{booking.notes}</p>
              </div>
            )}

            {booking.createdBy && (
              <div>
                <p className="text-sm font-medium text-gray-500">Booking Created By</p>
                <p className="font-semibold text-gray-900">{booking.createdBy.name}</p>
              </div>
            )}
          </div>
        )}

        {/* Organization Branding */}
        {booking.organization && (
          <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-center">
              <BuildingStorefrontIcon className="h-5 w-5 text-gray-400 mr-2" />
              <span className="text-gray-600">Provided by</span>
              <span className="ml-2 font-semibold text-gray-900">{booking.organization.name}</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            This is a secure booking confirmation. If you have questions, please contact the service provider.
          </p>
        </div>

      </div>
    </div>
  );
};

export default BookingView;